import { useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { PacmanLoader } from 'react-spinners';

import { useTranslation } from '@lichtblick/i18next';
import { Colors, TextVariants } from '@lichtblick/theme';

import { CheckIcon, Container, FileContainer, FlexColumn, MainContent, UploadFileIcon } from './UploadZone.styles';

import { Button } from '../Button';
import { Snackbar } from '../Snackbar';
import { Text } from '../Text';

const fileTypes = ['JPG', 'JPEG', 'PNG', 'docx', 'pdf', 'pptx', 'mp4', 'xlsx', 'xls'];

const MAX_FILE_COUNT = 50;
const MAX_TOTAL_SIZE_MB = 100;

const formatFileSize = (bytes: number) => {
  const KB = 1024;
  const MB = KB * 1024;

  if (bytes < 500 * KB) {
    return `${(bytes / KB).toFixed(2)} KB`;
  } else {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
};

type FileState = {
  file: File;
  isLoading: boolean;
  isSuccess: boolean;
};

type UploadZoneProps = {
  onUpload: (file: File[]) => Promise<boolean>;
};

export const UploadZone = ({ onUpload }: UploadZoneProps) => {
  const [files, setFiles] = useState<FileState[]>([]);
  const [shouldShowToast, setShouldShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  const { t } = useTranslation();

  const handleChange = async (inputFiles: File | File[] | FileList) => {
    setToastMessage('');
    const filesArray: File[] = getFilesAsArray(inputFiles);

    if (filesArray.length > MAX_FILE_COUNT) {
      setToastMessage(t('pm:maxFileCountExceeded', { count: MAX_FILE_COUNT }));
      setShouldShowToast(true);

      return;
    }

    const totalSizeMB = filesArray.reduce((total, file) => total + file.size, 0) / (1024 * 1024);

    if (totalSizeMB > MAX_TOTAL_SIZE_MB) {
      setToastMessage(t('pm:maxTotalSizeExceeded', { size: MAX_TOTAL_SIZE_MB }));
      setShouldShowToast(true);

      return;
    }

    const newFiles: FileState[] = filesArray.map((file) => ({
      file,
      isLoading: false,
      isSuccess: false,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    for (const fileState of newFiles) {
      try {
        setFiles((prev) => prev.map((f) => (f.file === fileState.file ? { ...f, isLoading: true } : f)));
        const isSuccess = await onUpload([fileState.file]);

        setFiles((prev) => prev.map((f) => (f.file === fileState.file ? { ...f, isLoading: false, isSuccess } : f)));
      } catch {
        setFiles((prev) =>
          prev.map((f) => (f.file === fileState.file ? { ...f, isLoading: false, isSuccess: false } : f)),
        );
        setToastMessage(t('pm:uploadFailed'));
        setShouldShowToast(true);
      }
    }
  };

  return (
    <Container>
      <FileUploader handleChange={handleChange} multiple={true} name="file" types={fileTypes}>
        <MainContent>
          <UploadFileIcon />
          <Text $variant={TextVariants.body.m}>{t('pm:dropFile')}</Text>
          <Text $variant={TextVariants.body.s}>{t('pm:or')}</Text>
          <Button label={t('pm:browse')} />
        </MainContent>
      </FileUploader>
      {files.map((fileState, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <FileContainer key={`${fileState.file.name}-${index}`}>
          <CheckIcon $isSuccess={fileState.isSuccess} />
          <FlexColumn>
            <Text
              $variant={TextVariants.body.s}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {fileState.file.name}
            </Text>
            <Text $variant={TextVariants.body.s} color={Colors.gray['50']}>
              {formatFileSize(fileState.file.size)}
            </Text>
          </FlexColumn>
          {fileState.isLoading && <PacmanLoader color={Colors.blue['100']} size={20} />}
        </FileContainer>
      ))}
      {files.length === 0 && <Text $variant={TextVariants.body.m}>{t('pm:noFiles')}</Text>}
      <Snackbar
        color="black"
        isOpen={shouldShowToast}
        message={toastMessage}
        setOpen={() => setShouldShowToast(false)}
        title={'Info'}
      />
    </Container>
  );
};

const getFilesAsArray = (inputFiles: File | File[] | FileList) => {
  if (Array.isArray(inputFiles)) {
    return inputFiles;
  } else if (inputFiles instanceof FileList) {
    return Array.from(inputFiles);
  }

  return [inputFiles];
};
